import {
    AccessTime as AccessTimeIcon,
    AttachFile as AttachFileIcon,
    CalendarToday as CalendarTodayIcon,
    CheckCircle as CheckCircleIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Comment as CommentIcon,
    Flag as FlagIcon,
    MoreVert as MoreVertIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import {
    Avatar,
    AvatarGroup,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Fade,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    MenuList,
    Paper,
    Popover,
    Tooltip,
    Typography,
    alpha
} from '@mui/material';
import React, { useEffect, useState } from "react";
import ImageLightbox2 from '../../ImageLightbox2';
import KanbanCardDrawer from '../../KanbanCardDrawer';
import ToastSuccess from '../../../toast/success/toastSuccess';
import ToastError from '../../../toast/error/toastError';
import KanbanTransferCardModal from '../../KanbanTransferCardModal';
import { useEvent } from '../../../hooks/useEvent';
import api from '../../../services/api';

const getColor = (theme, colorStr) => {
    if (typeof colorStr === 'string' && colorStr.includes('.')) {
        const [palette, variant] = colorStr.split('.');
        if (theme.palette[palette] && theme.palette[palette][variant]) {
            return theme.palette[palette][variant];
        }
    }
    return colorStr;
};

function truncateString(str, maxLength) {
    if (!str) return '';
    if (str.length <= maxLength) return str;
    return `${str.slice(0, maxLength)}...`;
}

const RichTooltip = ({ title, content, icon, color = "primary.main", children }) => {
    return (
        <Tooltip
            onClick={e => e.stopPropagation()}
            title={
                <Paper elevation={0} sx={{ p: 1, width: '100%', maxWidth: 280 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {icon && (
                            <Box sx={{ mr: 1, color: (theme) => getColor(theme, color) }}>
                                {icon}
                            </Box>
                        )}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: (theme) => getColor(theme, color) }}>
                            {title}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 0.75 }} />
                    <Box sx={{ pt: 0.5 }}>
                        {content}
                    </Box>
                </Paper>
            }
            arrow
            placement="top"
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        boxShadow: 3,
                        borderRadius: 1.5,
                        p: 0,
                        '& .MuiTooltip-arrow': {
                            color: 'background.paper',
                        },
                    }
                }
            }}
        >
            {children}
        </Tooltip>
    );
};

const EnhancedPopover = ({ title, content, icon, color = "primary.main", children }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const id = open ? 'enhanced-popover' : undefined;

    const handleClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (event) => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    return (
        <>
            <Box
                onClick={handleClick}
                sx={{
                    cursor: 'pointer',
                    display: 'inline-flex'
                }}
                aria-describedby={id}
            >
                {children}
            </Box>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                PaperProps={{
                    elevation: 2,
                    sx: {
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        width: 280
                    }
                }}
            >
                <Box>
                    <Box
                        sx={{
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: theme => {
                                const baseColor = getColor(theme, color);
                                return alpha(baseColor, theme.palette.mode === 'dark' ? 0.15 : 0.05);
                            },
                            borderBottom: 1,
                            borderColor: theme => {
                                const baseColor = getColor(theme, color);
                                return alpha(baseColor, theme.palette.mode === 'dark' ? 0.2 : 0.1);
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {icon && (
                                <Box sx={{
                                    color: theme => getColor(theme, color),
                                    mr: 1.5,
                                    display: 'flex'
                                }}>
                                    {icon}
                                </Box>
                            )}
                            <Typography variant="body1" sx={{
                                fontWeight: 600,
                                color: theme => getColor(theme, color)
                            }}>
                                {title}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={handleClose}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    bgcolor: 'action.hover'
                                }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                        {content}
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

const formatDateTime = (date) => {
    if (!date) return '';
    const options = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    return new Date(date).toLocaleString(undefined, options);
};

const formatDate = (date) => {
    if (!date) return '';
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(date).toLocaleDateString(undefined, options);
};

const getDueDateColor = (item) => {
    if (!item.dueDate) return "default";
    if (item.dueDateState === 'overdue') return "error";
    if (item.dueDateState === 'today') return "warning";
    return "success";
};

const getDueStateText = (state) => {
    switch (state) {
        case 'overdue': return "Atrasado";
        case 'today': return "Vence hoje";
        default: return "No prazo";
    }
};

const areEqual = (prevProps, nextProps) => {
    // Quick checks for primitive props first
    if (prevProps.item.id !== nextProps.item.id ||
        prevProps.isDragging !== nextProps.isDragging ||
        prevProps.isClone !== nextProps.isClone ||
        prevProps.columnId !== nextProps.columnId ||
        prevProps.kanbanId !== nextProps.kanbanId ||
        prevProps.name !== nextProps.name) {
        return false;
    }

    // Check loading state
    if (prevProps.loadingCard[prevProps.item.id] !== nextProps.loadingCard[nextProps.item.id]) {
        return false;
    }

    // Deep comparison for the item object (contains all card data)
    if (JSON.stringify(prevProps.item) !== JSON.stringify(nextProps.item)) {
        return false;
    }

    // Deep comparison for arrays and objects
    if (JSON.stringify(prevProps.priorities) !== JSON.stringify(nextProps.priorities) ||
        JSON.stringify(prevProps.tags) !== JSON.stringify(nextProps.tags) ||
        JSON.stringify(prevProps.assigneds) !== JSON.stringify(nextProps.assigneds) ||
        JSON.stringify(prevProps.displayFilters) !== JSON.stringify(nextProps.displayFilters)) {
        return false;
    }

    // If all checks pass, the components are equal
    return true;
};

const KanbanCardItem = React.memo(function KanbanCardItem({ item, columnId, kanbanId, taskTitle, isDragging, isClone, loadingCard, priorities, tags, assigneds, displayFilters }) {
    const emitter = useEvent();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget)
    };
    const handleClose = (event) => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    const [showFullChecklist, setShowFullChecklist] = useState(false);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [editModaOpen, setEditModaOpen] = useState(false);
    const [openTransferCard, setOpenTransferCard] = useState(false);

    const isCardLoading = React.useMemo(() => {
        if (!loadingCard || !item || !item.id) return false;

        if (loadingCard[item.id] === true) return true;

        const cardIdParts = item.id.split('-');
        if (cardIdParts.length > 1) {
            const columnId = `column-${cardIdParts[1]}`;
            return loadingCard[columnId] === true;
        }

        return false;
    }, [item, loadingCard]);


    const handleOpenLightbox = () => {
        setIsLightboxOpen(true);
    };

    const handleCloseLightbox = () => {
        setIsLightboxOpen(false);
    };

    const handleEditCard = (id) => {
        setEditModaOpen(true);
    };

    const handleArchive = async (item) => {
        try {
            const { data } = await api.put(`/kanban/card/${item.id.replace('card-', '')}`, { isArchived: true });
            console.info({ data })
            emitter.emit("kanban-card", {
                action: 'delete',
                cardId: item.id
            });
        } catch (err) {
            console.error(err)
        }
    };

    const copyToClipboard = (item) => {
        const formattedMessage = `*Protocolo*: ${item.taskNumber}\n*Chamado*: ${item.name}`;

        navigator.clipboard.writeText(formattedMessage).then(() => {
            ToastSuccess('Informações copiadas com sucesso!');
        }).catch(err => {
            ToastError('Erro ao copiar: ' + err.message);
        });
    };

    const handleTransferCard = async ({ kanbanId: newKanbanId, columnId, cardId }) => {
        try {
            if (kanbanId !== newKanbanId) {
                await api.put(`/kanban/card/transfer/${cardId}`, {
                    kanbanId: newKanbanId, columnId
                });
            } else {
                await api.put(`/kanban/card/${cardId}`, {
                    columnId
                });
            }
            emitter.emit("kanban-card", {
                action: 'transfer',
                kanbanId: newKanbanId,
                columnId: columnId,
                cardId: cardId
            });
        } catch (err) {
            console.error(err)
        }


    };


    console.info(item);

    return (
        <>
            <Card
                variant="outlined"
                sx={{
                    ...(isCardLoading && {
                        position: 'relative',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    }),
                    borderRadius: 1.5,
                    padding: '6px',
                    boxShadow: theme => isDragging
                        ? '0 8px 16px rgba(0,0,0,0.12)'
                        : theme.palette.mode === 'light'
                            ? '0 2px 4px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.07)'
                            : '0 1px 2px rgba(0,0,0,0.07)',
                    position: 'relative',
                    overflow: 'visible',
                    transition: 'all 0.2s ease-in-out',
                    borderColor: theme => theme.palette.mode === 'light'
                        ? 'rgba(0,0,0,0.08)'
                        : 'rgba(255,255,255,0.07)',
                    backgroundColor: theme => isDragging
                        ? theme.palette.mode === 'light'
                            ? '#F5F7FA'
                            : 'action.selected'
                        : theme.palette.mode === 'light'
                            ? '#FFFFFF'
                            : '#161c24',
                    transform: isDragging ? 'rotate(1deg) scale(1.02)' : 'none',
                    width: isClone ? 'calc(100% - 12px)' : '100%',
                    maxWidth: '280px',
                    '&:hover': {
                        boxShadow: theme => theme.palette.mode === 'light'
                            ? '0 4px 8px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.06)'
                            : '0 2px 6px rgba(0,0,0,0.12)',
                    }
                }}
                onClick={() => handleEditCard(item.id)}
            >
                {isCardLoading && (
                    <Fade in={isCardLoading}>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                zIndex: 10,
                                borderRadius: '12px',
                            }}
                        >
                            <CircularProgress
                                size={28}
                                thickness={3}
                                sx={{
                                    color: theme => theme.palette.primary.main
                                }}
                            />
                        </Box>
                    </Fade>
                )}

                {item.imageUrl && (
                    <Box sx={{
                        height: 90,
                        backgroundColor: '#f5f5f5',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderTopLeftRadius: 6,
                        borderTopRightRadius: 6,
                        overflow: 'hidden',
                        mb: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                            opacity: 0.9,
                        }
                    }}
                        onClick={handleOpenLightbox}
                    >
                        <img
                            src={item.imageUrl}
                            alt={item.title || "Imagem do card"}
                            loading="lazy"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    </Box>
                )}

                <CardContent sx={{
                    p: 1,
                    py: 0.75,
                    '&:last-child': { pb: 0.75 }
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, mt: 0.25, alignItems: 'flex-start' }}>
                        <Typography
                            variant="body1"
                            component="div"
                            fontWeight="600"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                wordBreak: 'break-word',
                                flexGrow: 1,
                                fontSize: '0.9rem',
                                lineHeight: 1.3,
                                color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.87)' : '#fff',
                            }}
                        >
                            {item.name}
                        </Typography>
                        <IconButton
                            id="menu-button"
                            size="small"
                            sx={{
                                ml: 0.25,
                                flexShrink: 0,
                                p: 0.25,
                                mt: -0.25,
                                color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.54)' : 'rgba(255,255,255,0.7)',
                                '&:hover': {
                                    backgroundColor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)',
                                }
                            }}
                            onClick={handleClick}
                        >
                            <MoreVertIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <Menu
                            id="menu-button"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            MenuListProps={{ 'aria-labelledby': 'menu-button', dense: true }}
                            PaperProps={{ sx: { minWidth: 180, p: 0 } }}
                        >
                            <MenuItem dense
                                onClick={(e) => {
                                    handleEditCard(item.id);
                                    handleClose(e);
                                }}
                            >
                                <ListItemIcon>
                                    <ListAltIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Abrir Tarefa" primaryTypographyProps={{ fontSize: 14 }} />
                            </MenuItem>
                            <MenuItem dense
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(item)
                                    handleClose(e);
                                }}
                            >
                                <ListItemIcon>
                                    <ContentCopyIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Copiar Protocolo" primaryTypographyProps={{ fontSize: 14 }} />
                            </MenuItem>
                            <MenuItem dense
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenTransferCard(true);
                                    handleClose(e);
                                }}
                            >
                                <ListItemIcon>
                                    <ControlCameraIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Transferir" primaryTypographyProps={{ fontSize: 14 }} />
                            </MenuItem>
                            <Divider sx={{ my: 0.5 }} />
                            <MenuItem dense
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClose(e);
                                    handleArchive(item)
                                }}
                            >
                                <ListItemIcon>
                                    <MoveToInboxIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Arquivar" primaryTypographyProps={{ fontSize: 14 }} />
                            </MenuItem>
                        </Menu>

                    </Box>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                        {displayFilters?.showPriorityName &&
                            <>
                                {item?.kanbanPriorityLevel && (
                                    <RichTooltip
                                        title="Prioridade"
                                        icon={<FlagIcon fontSize="small" />}
                                        color={item?.kanbanPriorityLevel.color}
                                        content={
                                            <Box>
                                                <Typography variant="body2">
                                                    Este card tem prioridade <strong>{item?.kanbanPriorityLevel.title}</strong>
                                                </Typography>
                                                <Box sx={{
                                                    mt: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: `${item?.kanbanPriorityLevel.color}15`,
                                                    py: 0.5,
                                                    borderRadius: 1
                                                }}>
                                                    <Box
                                                        sx={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            bgcolor: item?.kanbanPriorityLevel.color,
                                                            mr: 0.5
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: item?.kanbanPriorityLevel.color
                                                        }}
                                                    >
                                                        {item?.kanbanPriorityLevel.title}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                borderRadius: '7px',
                                                bgcolor: theme => `${theme.palette.mode === 'light'
                                                    ? item?.kanbanPriorityLevel.color
                                                    : item?.kanbanPriorityLevel.color}22`,
                                                px: 0.75,
                                                py: 0.25,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    bgcolor: theme => theme.palette.mode === 'light'
                                                        ? item?.kanbanPriorityLevel.color
                                                        : item?.kanbanPriorityLevel.color,
                                                    mr: 0.5
                                                }}
                                            />
                                            <Typography variant="caption" sx={{
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                                color: theme => theme.palette.mode === 'light'
                                                    ? item?.kanbanPriorityLevel.color
                                                    : item?.kanbanPriorityLevel.color,
                                            }}>
                                                {item?.kanbanPriorityLevel.title}
                                            </Typography>
                                        </Box>
                                    </RichTooltip>
                                )}
                            </>
                        }

                        {displayFilters?.showCardAuthorship && item?.creator && (
                            <EnhancedPopover
                                title="Autor do Card"
                                icon={<PersonIcon fontSize="small" />}
                                color="success.main"
                                content={
                                    <Box>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            p: 1.5,
                                            bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)',
                                            borderRadius: 1.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            mb: 2
                                        }}>
                                            <Avatar
                                                alt={item.creator.name}
                                                src={item.creator.media}
                                                sx={{
                                                    width: 42,
                                                    height: 42,
                                                    mr: 1.5,
                                                    border: '2px solid',
                                                    borderColor: theme => theme.palette.success.main
                                                }}
                                            />
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    {item.creator.name}
                                                </Typography>
                                                {item.creator.role && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {item.creator.role}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>

                                        <Typography variant="body2" color="text.secondary">
                                            Este card foi criado por este usuário.
                                        </Typography>
                                    </Box>
                                }
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)',
                                    borderRadius: '7px',
                                    px: 0.75,
                                    py: 0.25,
                                    border: '1px solid',
                                    borderColor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)',
                                }}>
                                    {item.creator.media ? (
                                        <Avatar
                                            alt={item.creator.name}
                                            src={item.creator.media}
                                            sx={{
                                                width: 16,
                                                height: 16,
                                                mr: 0.5,
                                                border: '1px solid',
                                                borderColor: theme => theme.palette.success.main
                                            }}
                                        />
                                    ) : (
                                        <PersonIcon sx={{ fontSize: 14, color: theme => theme.palette.success.main }} />
                                    )}
                                    <Typography variant="caption" sx={{
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
                                    }}>
                                        Criado por: {truncateString(item.creator.name, 20)}
                                    </Typography>
                                </Box>
                            </EnhancedPopover>
                        )}

                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75, mb: 0.75 }}>
                        {item.dueDate && (
                            <EnhancedPopover
                                title="Data de Vencimento"
                                icon={<CalendarTodayIcon fontSize="small" />}
                                color={getDueDateColor(item) === "error" ? "error.main" :
                                    getDueDateColor(item) === "warning" ? "warning.main" : "success.main"}
                                content={
                                    <Box>
                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                            Data limite para conclusão deste card
                                        </Typography>

                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 1.5,
                                                mb: 2,
                                                borderRadius: 1.5,
                                                bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.05)',
                                                border: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <CalendarTodayIcon
                                                    fontSize="small"
                                                    sx={{
                                                        mr: 1,
                                                        color: theme => {
                                                            const color = getDueDateColor(item);
                                                            return color === "error" ? theme.palette.error.main :
                                                                color === "warning" ? theme.palette.warning.main :
                                                                    theme.palette.success.main;
                                                        }
                                                    }}
                                                />
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {formatDateTime(item.dueDate)}
                                                </Typography>
                                            </Box>

                                            <Chip
                                                label={getDueStateText(item.dueDateState)}
                                                size="small"
                                                color={getDueDateColor(item)}
                                                sx={{
                                                    height: 24,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500
                                                }}
                                            />
                                        </Paper>

                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                            Acompanhe o prazo desta tarefa e planeje-se adequadamente para evitar atrasos.
                                        </Typography>
                                    </Box>
                                }
                            >
                                <Chip
                                    size="small"
                                    icon={<AccessTimeIcon sx={{ fontSize: 12 }} />}
                                    label={formatDate(item.dueDate)}
                                    color={getDueDateColor(item)}
                                    variant="outlined"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        '.MuiChip-label': { px: 0.75 },
                                        fontWeight: 500,
                                    }}
                                />
                            </EnhancedPopover>
                        )}
                    </Box>
                    {item?.progress && (
                        <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" fontWeight="bold" sx={{
                                    fontSize: '0.7rem',
                                    color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
                                }}>
                                    {item.progress}%
                                </Typography>
                                <Box
                                    sx={{
                                        height: 4,
                                        width: '100%',
                                        bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                                        borderRadius: 2
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: `${item.progress}%`,
                                            bgcolor: item.progress === 100 ? 'success.main' : 'primary.main',
                                            borderRadius: 2
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                        {displayFilters?.showTags &&
                            <>
                                {item?.tags?.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                                        {formatTags(item?.tags).map((label) => (
                                            <Box
                                                key={label.id}
                                                sx={{
                                                    bgcolor: `${label.color}15`,
                                                    color: label.color,
                                                    borderRadius: '7px',
                                                    px: 0.75,
                                                    py: 0.25,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 500,
                                                    border: '1px solid',
                                                    borderColor: `${label.color}30`,
                                                }}
                                            >
                                                {label.title}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </>
                        }
                        <Box sx={{ mb: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {item?.requester &&
                                <EnhancedPopover
                                    title="Solicitante"
                                    icon={<PersonIcon fontSize="small" />}
                                    color="info.main"
                                    content={
                                        <Box>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                p: 1.5,
                                                bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)',
                                                borderRadius: 1.5,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                mb: 2
                                            }}>
                                                <Avatar
                                                    alt={item.requester.name}
                                                    src={item.requester.profilePicUrl}
                                                    sx={{
                                                        width: 42,
                                                        height: 42,
                                                        mr: 1.5,
                                                        border: '2px solid',
                                                        borderColor: theme => theme.palette.info.main
                                                    }}
                                                />
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                        {item.requester.name}
                                                    </Typography>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        mt: 0.25
                                                    }}>
                                                        <PhoneIcon sx={{ fontSize: 14, color: theme => theme.palette.info.main }} />
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {item.requester.number}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            <Typography variant="body2" color="text.secondary">
                                                Este card foi solicitado por este contato.
                                                Clique no cartão para ver mais detalhes sobre a solicitação.
                                            </Typography>
                                        </Box>
                                    }
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)',
                                        borderRadius: '7px',
                                        px: 0.75,
                                        py: 0.25,
                                        border: '1px solid',
                                        borderColor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)',
                                    }}>
                                        <PersonIcon sx={{ fontSize: 14, color: theme => theme.palette.info.main }} />
                                        <Typography variant="caption" sx={{
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
                                        }}>
                                            {truncateString(item.requester.name, 15)}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                                            <PhoneIcon sx={{ fontSize: 12, color: theme => theme.palette.info.main }} />
                                        </Box>
                                    </Box>
                                </EnhancedPopover>
                            }
                        </Box>
                    </Box>
                    {item.description && (
                        <Typography
                            variant="body2"
                            color={theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.65)' : 'text.secondary'}
                            sx={{
                                mb: 0.75,
                                fontSize: '0.75rem',
                                lineHeight: 1.4,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {item.description}
                        </Typography>
                    )}

                    {item?.customFields && item?.customFields?.length > 0 && (
                        <Box sx={{ mb: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {item.customFields.map((field, index) => (
                                <Box
                                    key={`custom-${index}`}
                                    sx={{
                                        fontSize: '0.7rem',
                                        bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)',
                                        borderRadius: 1,
                                        px: 0.75,
                                        py: 0.25,
                                        border: '1px solid',
                                        borderColor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)',
                                    }}
                                >
                                    <Typography component="span" sx={{
                                        fontWeight: 600,
                                        fontSize: 'inherit',
                                        color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
                                    }}>
                                        {field.name}:
                                    </Typography>{' '}
                                    <Typography component="span" sx={{
                                        fontSize: 'inherit',
                                        color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
                                    }}>
                                        {field.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {item?.tasks?.length > 0 && (
                        <Box sx={{ mb: 0.75 }}>
                            {/* Barra de progresso global */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                                <Typography variant="caption" sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
                                }}>
                                    {/* Calculando o total de itens completos / total de itens */}
                                    {item.tasks.reduce((total, task) =>
                                        total + task.checklists.filter(item => item.isCompleted).length, 0
                                    )}/
                                    {item.tasks.reduce((total, task) =>
                                        total + task.checklists.length, 0
                                    )}
                                </Typography>
                                <Box
                                    sx={{
                                        height: 3,
                                        width: '100%',
                                        bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)',
                                        borderRadius: 2
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: `${(() => {
                                                const completed = item.tasks.reduce((total, task) =>
                                                    total + task.checklists.filter(item => item.isCompleted).length, 0);
                                                const total = item.tasks.reduce((total, task) =>
                                                    total + task.checklists.length, 0);
                                                return total > 0 ? (completed / total) * 100 : 0;
                                            })()}%`,
                                            bgcolor: theme => {
                                                const completed = item.tasks.reduce((total, task) =>
                                                    total + task.checklists.filter(item => item.isCompleted).length, 0);
                                                const total = item.tasks.reduce((total, task) =>
                                                    total + task.checklists.length, 0);
                                                return (total > 0 && completed === total) ? theme.palette.success.main : theme.palette.primary.main;
                                            },
                                            borderRadius: 2
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ pl: 0.25 }}>
                                {item.tasks.flatMap(task =>
                                    task.checklists.map(checkItem => ({
                                        ...checkItem,
                                        taskTitle: task.title
                                    }))
                                ).slice(0, showFullChecklist ? undefined : 1).map((checkItem, index) => (
                                    <Box
                                        key={checkItem.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 0.75,
                                            mb: 0.5,
                                            opacity: checkItem.isCompleted ? 0.65 : 1,
                                        }}
                                    >
                                        {checkItem.isCompleted ? (
                                            <CheckCircleIcon sx={{
                                                fontSize: 14,
                                                color: theme => theme.palette.success.main,
                                                mt: 0.25,
                                                flexShrink: 0
                                            }} />
                                        ) : (
                                            <CheckIcon sx={{
                                                fontSize: 14,
                                                color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.3)' : 'text.disabled',
                                                mt: 0.25,
                                                flexShrink: 0
                                            }} />
                                        )}
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    textDecoration: checkItem.isCompleted ? 'line-through' : 'none',
                                                    color: checkItem.isCompleted
                                                        ? theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.4)' : 'text.disabled'
                                                        : theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.7)' : 'text.primary',
                                                    fontSize: '0.7rem',
                                                    lineHeight: 1.4,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 1,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                {checkItem.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                            {item.tasks.reduce((total, task) => total + task.checklists.length, 0) > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.25 }}>
                                    <Typography
                                        variant="caption"
                                        color="primary.main"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowFullChecklist(!showFullChecklist);
                                        }}
                                        sx={{
                                            fontSize: '0.65rem',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            display: 'inline-block',
                                            ...(showFullChecklist ? {
                                                px: 1,
                                                py: 0.25,
                                                bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.15)',
                                                borderRadius: 1,
                                                '&:hover': {
                                                    bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.25)',
                                                }
                                            } : {
                                                '&:hover': {
                                                    textDecoration: 'underline'
                                                }
                                            })
                                        }}
                                    >
                                        {showFullChecklist ? 'Mostrar menos' : `+${item.tasks.reduce((total, task) => total + task.checklists.length, 0) - 1} mais`}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 0.75,
                            borderTop: theme => `1px solid ${theme.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.04)'}`,
                            pt: 0.75
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {item?.comments?.total > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                    <CommentIcon sx={{
                                        fontSize: 14,
                                        color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.5)' : 'text.secondary'
                                    }} />
                                    <Typography variant="caption" sx={{
                                        fontSize: '0.7rem',
                                        color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
                                    }}>
                                        {item?.comments.total}
                                    </Typography>
                                </Box>
                            )}
                            {item?.comments?.medias > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                    <AttachFileIcon sx={{
                                        fontSize: 14,
                                        color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.5)' : 'text.secondary'
                                    }} />
                                    <Typography variant="caption" sx={{
                                        fontSize: '0.7rem',
                                        color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
                                    }}>
                                        {item?.comments.medias}
                                    </Typography>
                                </Box>
                            )}

                            {item?.views > 0 && (
                                <EnhancedPopover
                                    title="Visualizações"
                                    icon={<VisibilityIcon fontSize="small" />}
                                    content={
                                        <Box>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                p: 2,
                                                bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)',
                                                borderRadius: 2,
                                                mb: 2,
                                                border: '1px solid',
                                                borderColor: 'divider'
                                            }}>
                                                <Typography variant="h4" sx={{
                                                    fontWeight: 600,
                                                    color: theme => theme.palette.primary.main
                                                }}>
                                                    {item.views}
                                                </Typography>
                                                <Typography variant="body1" sx={{ ml: 1 }}>
                                                    visualizações
                                                </Typography>
                                            </Box>

                                            {item?.lastViewed && (
                                                <Box sx={{
                                                    p: 1.5,
                                                    bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)',
                                                    borderRadius: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '1px solid',
                                                    borderColor: 'divider'
                                                }}>
                                                    <AccessTimeIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                            Última visualização
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {formatDateTime(item.lastViewed)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    }
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.7rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: theme => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
                                        }}
                                    >
                                        👁️ {item.views}
                                    </Typography>
                                </EnhancedPopover>
                            )}
                        </Box>

                        {item?.assigned?.length > 0 && (
                            <RichTooltip
                                title="Pessoas Atribuídas"
                                icon={<PersonIcon fontSize="small" />}
                                content={
                                    <Box>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {item.assigned.length} pessoa{item.assigned.length !== 1 ? 's' : ''} atribuída{item.assigned.length !== 1 ? 's' : ''} a este card:
                                        </Typography>

                                        <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
                                            {item.assigned.map((user) => (
                                                <Box
                                                    key={user.id}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        p: 0.75,
                                                        mb: 0.5,
                                                        borderRadius: 1,
                                                        backgroundColor: 'background.default'
                                                    }}
                                                >
                                                    <Avatar
                                                        alt={user.name}
                                                        src={user.media}
                                                        sx={{ width: 24, height: 24, mr: 1 }}
                                                    />
                                                    <Box>
                                                        <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                                                            {user.name}
                                                        </Typography>
                                                        {user.role && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                                                {user.role}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                }
                            >
                                <AvatarGroup
                                    max={3}
                                    sx={{
                                        '& .MuiAvatar-root': {
                                            width: 24,
                                            height: 24,
                                            fontSize: '0.75rem',
                                            border: theme => `2px solid ${theme.palette.mode === 'light' ? '#fff' : '#161c24'}`,
                                            boxShadow: theme => theme.palette.mode === 'light' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                                        }
                                    }}
                                >
                                    {item.assigned.map((user) => (
                                        <Avatar
                                            key={user.id}
                                            alt={user.name}
                                            src={user.media}
                                        />
                                    ))}
                                </AvatarGroup>
                            </RichTooltip>
                        )}
                    </Box>
                </CardContent>
            </Card >
            <KanbanTransferCardModal
                columnId={columnId}
                cardId={item.id.replace('card-', '')}
                open={openTransferCard}
                onClose={() => setOpenTransferCard(false)}
                onConfirm={handleTransferCard}
            />
            <KanbanCardDrawer
                open={editModaOpen}
                onClose={() => setEditModaOpen(false)}
                cardId={item.id.replace('card-', '')}
                priorities={priorities}
                cardTags={tags}
                assigneds={assigneds}
            />
            {
                item?.imageUrl && (
                    <ImageLightbox2
                        src={item.imageUrl}
                        alt={item.title || "Imagem do card"}
                        title={item.title}
                        description={item.description}
                        open={isLightboxOpen}
                        onClose={handleCloseLightbox}
                    />
                )
            }
        </>
    );
}, areEqual);

export default KanbanCardItem;

function formatTags(tags) {
    // Se não existir ou for nulo, retorna array vazio
    if (!tags) return [];

    try {
        // Se for string, tentar converter
        if (typeof tags === 'string') {
            // Caso seja uma string JSON válida, ex: '[{ "name": "tag1" }]'
            if (tags.trim().startsWith('[') || tags.trim().startsWith('{')) {
                const parsed = JSON.parse(tags);
                // Garante que o retorno seja um array
                return Array.isArray(parsed) ? parsed : [parsed];
            } else {
                // Caso seja apenas uma string comum (ex: "tag1, tag2")
                return tags.split(',').map(t => t.trim()).filter(Boolean);
            }
        }

        // Se já for um array, retorna como está
        if (Array.isArray(tags)) return tags;

        // Se for um objeto único
        if (typeof tags === 'object') return [tags];

        // Caso contrário, retorna vazio
        return [];
    } catch (e) {
        console.error('Erro ao formatar tags:', e);
        return [];
    }
}
